import { alpha, Box, Chip, Typography, useTheme } from "@mui/material";
import React, { FC, isValidElement, SVGProps } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import IconWrapper from "../../../common/components/functional/icons/IconWrapper";
import { getTalentStatusIcon } from "../../../common/components/functional/talentStatus/TalentStatusIcons";
import { formatDateTimeRelativeToNow } from "../../../common/helpers/formatDateTime";
import { ReactComponent as AlertIcon } from "../../../common/icons/alertWarning.svg";
import { ReactComponent as PhoneIcon } from "../../../common/icons/phone.svg";
import { resolveConversationTitle } from "../conversation/conversationTitle";
import { isCustomerConversation, isShiftNotificationMessage } from "../helpers";
import { inboxTabForConversation } from "../inboxTabs";
import {
  selectConversationSearchQuery,
  selectTopicsById,
} from "../messagingSlice";
import { SenderDetails } from "../types/common";
import { Conversation, Participant } from "../types/queries";
import InboxRowLastMessage from "./InboxRowLastMessage";
import InboxRowPrimaryText from "./InboxRowPrimaryText";
import InboxRowSecondaryText from "./InboxRowSecondaryText";
import UnreadMessageCount from "./UnreadMessageCount";

type Props = {
  conversation: Conversation;
  hasMultipleParticipants: boolean;
  singleOtherParticipant?: Participant | null;
  supportMessageSenderParticipant?: Participant | null;
  isTalentConversation: boolean;
};

const InboxRowListItemTexts = ({
  conversation,
  hasMultipleParticipants,
  singleOtherParticipant,
  supportMessageSenderParticipant,
  isTalentConversation,
}: Props) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation("messaging");
  const searchQuery = useAppSelector(selectConversationSearchQuery);
  const topicsById = useAppSelector(selectTopicsById);
  const searchTab = searchQuery ? inboxTabForConversation(conversation) : null;
  const searchChip = searchTab
    ? {
        teamOpen: {
          labelKey: "teamOpenTab",
          backgroundColor: theme.palette.primary.light,
          color: "#FFF",
        },
        myOpen: {
          labelKey: "openTab",
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
        inProgress: {
          labelKey: "inProgressTab",
          backgroundColor: alpha(theme.palette.warning.main, 0.7),
          color: theme.palette.warning.contrastText,
        },
        closed: {
          labelKey: "closedTab",
          backgroundColor: alpha(theme.palette.success.main, 0.5),
          color: theme.palette.success.contrastText,
        },
      }[searchTab]
    : null;

  const isShiftNotificationMessageType = isShiftNotificationMessage(
    conversation.messageTemplate,
  );
  const hideConversationTitle = isCustomerConversation({
    conversationType: conversation.type,
    participantTypes: conversation.participants.map(
      (participant) => participant.type,
    ),
  });
  const conversationTitle =
    isShiftNotificationMessageType || hideConversationTitle
      ? undefined
      : resolveConversationTitle({
          topic: conversation.topic,
          topicId: conversation.topicId,
          topicsById,
          language: i18n.language,
        });
  const getParticipantStatusIcon = (
    participant?: Participant | null,
  ): React.ReactNode => {
    return participant?.talentStatus
      ? getTalentStatusIcon(participant?.talentStatus)
      : null;
  };

  const getParticipantWorkLocations = (
    participant?: Participant | null,
  ): string => {
    return participant?.customerWorkLocationNames?.length
      ? " - " + participant.customerWorkLocationNames.join(", ")
      : "";
  };

  const getSenderDetails = (): SenderDetails => {
    if (isShiftNotificationMessageType) {
      const splitTopic = (conversation?.topic || "").split("/");
      const topic = splitTopic[0] || "";
      const workLocation = splitTopic[1] ? splitTopic[1] : "";

      const fulfilled = conversation.messageTemplate?.includes("FULFILLED");

      return {
        topic: topic,
        details: workLocation,
        icon: fulfilled ? PhoneIcon : AlertIcon,
        messageTemplate: conversation.messageTemplate,
      };
    }
    if (hasMultipleParticipants) {
      const icon = getParticipantStatusIcon(supportMessageSenderParticipant);
      const workLocations = getParticipantWorkLocations(
        supportMessageSenderParticipant,
      );
      return {
        name: supportMessageSenderParticipant
          ? `${supportMessageSenderParticipant?.firstName} ${supportMessageSenderParticipant?.lastName}${workLocations}`
          : "",
        details: supportMessageSenderParticipant
          ? `${isTalentConversation ? supportMessageSenderParticipant?.city || "" : supportMessageSenderParticipant?.customerCompanyName || ""}`
          : "",
        icon,
      };
    }
    const icon = getParticipantStatusIcon(singleOtherParticipant);
    const workLocations = getParticipantWorkLocations(singleOtherParticipant);
    return {
      name: singleOtherParticipant
        ? `${singleOtherParticipant?.firstName} ${singleOtherParticipant?.lastName}${workLocations}`
        : "",
      details: singleOtherParticipant
        ? `${isTalentConversation ? supportMessageSenderParticipant?.city || "" : supportMessageSenderParticipant?.customerCompanyName || ""}`
        : "",
      icon,
    };
  };

  const senderDetails: SenderDetails = getSenderDetails();

  const renderIcon = () => {
    if (!senderDetails.icon) return null;
    if (isValidElement(senderDetails.icon))
      return (
        <Box display="flex" alignItems="center">
          {senderDetails.icon}
        </Box>
      );
    return (
      <IconWrapper
        icon={senderDetails.icon as FC<SVGProps<SVGSVGElement>>}
        sx={{
          display: "block",
          fontSize: "18px",
          width: "18px",
          height: "18px",
          ...(isShiftNotificationMessageType && {
            "& path": { fill: theme.palette.error.main },
          }),
        }}
      />
    );
  };

  return (
    <Box
      display="flex"
      flex={1}
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="space-between"
      minWidth={0}
      overflow="visible"
    >
      <Box
        display="flex"
        alignItems="center"
        gap="8px"
        width="100%"
        minWidth={0}
        overflow="visible"
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 0, overflow: "visible" }}>
          <InboxRowPrimaryText
            senderDetails={senderDetails}
            renderIcon={renderIcon}
          />
        </Box>
        {searchChip && (
          <Chip
            size="small"
            label={t(searchChip.labelKey)}
            sx={{
              height: 20,
              flexShrink: 0,
              margin: 0,
              "&&": {
                backgroundColor: searchChip.backgroundColor,
                color: searchChip.color,
              },
              "& .MuiChip-label": {
                paddingLeft: "6px",
                paddingRight: "6px",
                fontSize: "11px",
              },
            }}
          />
        )}
        <Typography
          variant="caption"
          color="text.disabled"
          fontSize="14px"
          sx={{ flexShrink: 0, whiteSpace: "nowrap", lineHeight: 1 }}
        >
          {conversation.createdAt &&
            formatDateTimeRelativeToNow(conversation.createdAt, {
              labels: {
                today: t("today"),
                yesterday: t("yesterday"),
              },
            })}
        </Typography>
      </Box>
      <Box
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: "14px",
          lineHeight: "20px",
          color: "text.secondary",
        }}
      >
        <InboxRowSecondaryText
          senderDetails={senderDetails}
          renderIcon={renderIcon}
        />
      </Box>
      {conversationTitle && (
        <Typography
          variant="body2"
          noWrap
          sx={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: "20px",
            color: "text.primary",
            fontWeight: !!conversation.unreadMessageCount ? 600 : 400,
          }}
        >
          {conversationTitle}
        </Typography>
      )}
      <Box
        display="flex"
        flex={1}
        alignItems="flex-start"
        gap="8px"
        minWidth={0}
      >
        <InboxRowLastMessage conversation={conversation} />
        {!!conversation.unreadMessageCount && (
          <Box sx={{ flexShrink: 0 }}>
            <UnreadMessageCount count={conversation.unreadMessageCount} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InboxRowListItemTexts;
